import React, { useState, FC } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "../../inputs/ByzMuiTypography";
import CheckIcon from '@mui/icons-material/Check';

interface ByzMuiSaveButtonProps extends React.ComponentProps<typeof Button> {
  onClick?: () => void;
  link?: string;
  popup?: boolean;
  loading?: boolean;
  tooltip?: string;
}

const StyledButton = styled(Button)<{ popup?: boolean }>(
  ({ popup, disabled, theme }) => ({
    borderRadius: popup ? "20px" : "4px",
    padding: "4px 20px",
    gap: "4px",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${disabled ? theme.palette.action.disabled : theme.palette.primary.main}`,
    backgroundColor: disabled
      ? alpha(theme.palette.action.disabled, 0.2)
      : alpha(theme.palette.primary.main, 0.1),
    color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
    cursor: disabled ? 'not-allowed' : 'pointer',
    "&:hover": {
      backgroundColor: "#2D9435",
      color: "#fff",
    },
  })
);

const ByzMuiSaveButton: FC<ByzMuiSaveButtonProps> = ({
  onClick,
  link,
  popup,
  disabled,
  loading = false,
  children,
  tooltip,
  ...rest
}) => {
  const { t } = useTranslate();
  const [hover, setHover] = useState(false);

  const btnContent = (
    <StyledButton
      popup={popup}
      disabled={disabled || loading}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...rest}
    >
      {loading ? (
        <CircularProgress
          size={16}
          sx={{
            color: "inherit",
            marginRight: "8px"
          }}
        />
      ) : (
        <CheckIcon
          style={{
            width: "16px",
            height: "16px",
            transition: "0.2s ease-in-out",
          }}
        />
      )}
      <ByzMuiTypography fontWeight={400} variant="body2" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {loading ? t("buttons.saving") : (children || t("buttons.save"))}
      </ByzMuiTypography>
    </StyledButton>
  );

  const btnWithTooltip = tooltip ? (
    <Tooltip 
      title={tooltip} 
      arrow 
      PopperProps={{
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "window",
            },
          },
        ],
      }}
    >
      <span style={{ display: "inline-block" }}>{btnContent}</span>
    </Tooltip>
  ) : (
    btnContent
  );

  return link ? (
    <Link to={link || ""} style={{ textDecoration: "none" }}>{btnWithTooltip}</Link>
  ) : (
    btnWithTooltip
  );
};

export default ByzMuiSaveButton;